import { Injectable, signal } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { ApiService } from './api.service';

export type ProjectContextRole = 'PROJECT_ADMIN' | 'MEMBER' | 'VIEWER' | null;

@Injectable({ providedIn: 'root' })
export class ProjectContextService {
  private readonly roleByProjectId = signal<Record<number, ProjectContextRole | undefined>>({});

  constructor(private api: ApiService) {}

  setRole(projectId: number, role: ProjectContextRole): void {
    if (!projectId || projectId <= 0) return;
    this.roleByProjectId.update((m) => ({ ...m, [projectId]: role }));
  }

  getRole(projectId: number): ProjectContextRole | undefined {
    return this.roleByProjectId()[projectId];
  }

  isViewer(projectId: number): boolean {
    return this.getRole(projectId) === 'VIEWER';
  }

  isProjectAdmin(projectId: number): boolean {
    return this.getRole(projectId) === 'PROJECT_ADMIN';
  }

  ensureLoaded(projectId: number): Observable<ProjectContextRole> {
    if (!projectId || projectId <= 0) return of(null);
    const existing = this.getRole(projectId);
    if (existing !== undefined) return of(existing ?? null);

    return this.api
      .get<{ my_context?: { project_role?: string } }>('/projects/' + projectId)
      .pipe(
        map((res) => (res.status && res.data ? this.normalizeRole(res.data?.my_context?.project_role) : null)),
        tap((role) => this.setRole(projectId, role)),
      );
  }

  private normalizeRole(value: unknown): ProjectContextRole {
    const v = String(value || '').trim().toUpperCase();
    if (v === 'PROJECT_ADMIN' || v === 'MEMBER' || v === 'VIEWER') return v;
    return null;
  }
}

